import React from 'react';
import { Helmet } from 'react-helmet';

const {
  NODE_ENV,
  URL: NETLIFY_SITE_URL = 'https://iziwork.com/',
  DEPLOY_PRIME_URL: NETLIFY_DEPLOY_URL = NETLIFY_SITE_URL,
  CONTEXT: NETLIFY_ENV = NODE_ENV,
} = process.env;
const isNetlifyProduction = NETLIFY_ENV === 'production';
const siteUrl = isNetlifyProduction ? NETLIFY_SITE_URL : NETLIFY_DEPLOY_URL;

export default function SEO({ data, post, homepage }) {
  const url = siteUrl + data.polylang_current_lang + (post === true ? '/blog' : '') + (homepage === true ? '' : `/${data.slug}`);
  const faqLdJson = buildFaqLdJson(data);
  const articleLdJson = buildArticleLdJson(data, url);
  const pageLdJson = buildPageLdJson(data, url);

  let title, metadesc, image;
  if (data.yoast.title) {
    title = data.yoast.title.replace('%%sitename%%', '').replace('%%sep%%', '').replace('&#8211;', "-");
  } else {
    title = data.title.replace('&#8211;', "-").replace('&rsquo;', "'") + ' - iziwork';
  }
  if (data.yoast.metadesc) {
    metadesc = data.yoast.metadesc;
  } else {
    metadesc = data.excerpt;
  }
  if (data.featured_media) {
    image = data.featured_media.source_url;
  }
  if (data.acf.featured_thumbnail) {
    image = data.acf.featured_thumbnail.source_url;
  }

  return (
    <Helmet>
      <html lang={data.polylang_current_lang} defer={true} />
      <title>{!post ? title : title + ' - iziwork'}</title>
      <meta name="description" content={metadesc} />
      <meta name="image" content={image} />
      <meta httpEquiv="content-language" content={data.polylang_current_lang} />
      <meta name="apple-itunes-app" content="app-id=1446760096" />
      <meta name="google-play-app" content="app-id=abeka.iziwork" />
      <link rel="canonical" href={url} />
      <link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.5.2/css/bootstrap.min.css" integrity="sha384-JcKb8q3iqJ61gNV9KGb8thSsNjpSL0n8PARn9HuZOnIxN0hoP+VmmDGMN5t9UJ0Z" crossorigin="anonymous" />

      {/* OpenGraph tags */}
      <meta property="og:type" content={articleLdJson ? 'article' : 'website'} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metadesc} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="900" />
      <meta property="og:image:height" content="600" />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content="@iziworkFR" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metadesc} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:app:name:iphone" content="iziwork" />
      <meta name="twitter:app:id:iphone" content="1446760096" />
      <meta name="twitter:app:url:iphone" content="https://itunes.apple.com/us/app/iziwork-offres-demploi/id1446760096?l=fr&ls=1&mt=8" />
      <meta name="twitter:app:name:googleplay" content="iziwork" />
      <meta name="twitter:app:id:googleplay" content="abeka.iziwork" />

      {data.acf.faq_post ? faqLdJson : null}
      {!data.acf.faq_post && post ? articleLdJson : null}
      {!post ? pageLdJson : null}
    </Helmet>
  );
};

/**
 * Build the basic page LD+JSON
 */
const buildPageLdJson = (data, url) => {
  return (
    <script type="application/ld+json">
      {`
      {
        "@context": "http://schema.org",
        "@type": "WebPage",
        "url": "${url}",
        "name": "${data.title}"
      }
      `}
    </script>
  );
};

/**
 * Format faqs in the corresponding LD+JSON format
 * @param {Array} faq
 */
const buildFaqLdJson = (data) => {
  let faq = [];
  if (data.acf.faq_post) {
    faq = data.acf.faq_post.map((item) => `
    {
      "@type": "Question",
      "name": "${item.question}",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "${item.answer}"
      }
    }`);
  }

  return (
    <script type="application/ld+json">
      {`
      {
        "@context": "http://schema.org",
        "@type": "FAQPage",
        "mainEntity": [${faq.join(', ')}]
      }
    `}
    </script>
  );
};

/**
 * Build the basic article LD+JSON
 */
const buildArticleLdJson = (data, url) => {
  return (
    <script type="application/ld+json">
      {`
      {
        "@context": "http://schema.org",
        "@type": "Blog",
        "headline": "${data.title}",
        "url": "${url}",
        "image": {
          "@type": "ImageObject",
          "url": "${data.featured_media ? data.featured_media.source_url : null}",
          "width": "900",
          "height": "600"
        },
        "datePublished": "${data.date}",
        "dateModified": "${data.date}",
        "author": "iziwork",
        "publisher": {
          "@type": "Organization",
          "name": "iziwork",
          "logo": {
            "@type": "ImageObject",
            "url": "https://wordpress-prod.iziwork.com/wp-content/uploads/2020/08/Brand_izi-icon-rounded.svg"
          }
        },
        "description": "${data.excerpt}"
      }
      `}
    </script>
  );
};